import React, { useEffect, useState } from 'react';
import {
  Library,
  Upload as UploadIcon,
  RefreshCw,
  X,
  FolderPlus,
  Plus,
  FileText,
  FileType,
  Link as LinkIcon,
  Trash2,
  Pencil,
  Check,
} from 'lucide-react';
import './Dashboard.css';

// Use agent API (port 3016) only — same as Chatbot/BrowserVoice; empty string uses dev proxy
const API_BASE = process.env.REACT_APP_AGENT_API_URL || '';

const FILE_TYPES = [
  { id: 'text', label: 'Text', ext: '.txt', accept: '.txt,text/plain', icon: FileText },
  { id: 'pdf', label: 'PDF', ext: '.pdf', accept: '.pdf,application/pdf', icon: FileType },
  { id: 'word', label: 'Word', ext: '.docx', accept: '.doc,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document', icon: FileType },
  { id: 'ppt', label: 'PowerPoint', ext: '.pptx', accept: '.pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation', icon: FileType },
];

const ALL_ACCEPT = FILE_TYPES.map((t) => t.accept.split(',')).flat().join(',');

const UploadKnowledgeBase = () => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [collectionMode, setCollectionMode] = useState('new');
  const [newCollectionName, setNewCollectionName] = useState('');
  const [selectedCollectionId, setSelectedCollectionId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [collections, setCollections] = useState([]);
  const [filesByCollection, setFilesByCollection] = useState({});
  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  })();
  const [deletingId, setDeletingId] = useState(null);
  const [editingCollectionId, setEditingCollectionId] = useState('');
  const [renameValue, setRenameValue] = useState('');
  const [renamingId, setRenamingId] = useState('');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [urlUploading, setUrlUploading] = useState(false);
  const [addSourceType, setAddSourceType] = useState('file');
  const [selectedFileType, setSelectedFileType] = useState('pdf');
  const fileInputRef = React.useRef(null);

  const handleFileChange = (e) => {
    const fileList = Array.from(e.target.files || []);
    setSelectedFiles(fileList);
    setMessage('');
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const fetchCollections = async () => {
    try {
      const ownerId = currentUser?.id || currentUser?._id || currentUser?.userId || null;
      const query = ownerId ? `?ownerId=${encodeURIComponent(ownerId)}` : '';
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/knowledgebase-collections${query}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      if (data.success) setCollections(data.collections || []);
    } catch (err) {
      console.error('Error fetching collections:', err);
    }
  };

  const fetchFiles = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = `${API_BASE}/api/knowledgebase`;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      if (!res.ok) {
        console.error('Knowledge base list failed:', res.status, data);
        setFilesByCollection({});
        return;
      }
      if (!data.success) {
        setFilesByCollection({});
        return;
      }
      const grouped = {};
      const ungrouped = [];
      (data.files || []).forEach((file) => {
        if (file.collectionId) {
          const cid = String(file.collectionId);
          if (!grouped[cid]) grouped[cid] = [];
          grouped[cid].push(file);
        } else {
          ungrouped.push(file);
        }
      });
      if (ungrouped.length > 0) grouped['_ungrouped'] = ungrouped;
      setFilesByCollection(grouped);
    } catch (err) {
      console.error('Error fetching files:', err);
      setFilesByCollection({});
    }
  };

  useEffect(() => {
    fetchCollections();
    fetchFiles();
  }, []);

  const handleUpload = async () => {
    setMessage('');
    if (selectedFiles.length === 0) {
      setMessage('Please select at least one file.');
      return;
    }
    if (collectionMode === 'new' && !newCollectionName.trim()) {
      setMessage('Please enter a collection name.');
      return;
    }
    if (collectionMode === 'existing' && !selectedCollectionId) {
      setMessage('Please select a collection.');
      return;
    }
    try {
      setUploading(true);
      setMessage('⏳ Processing files and generating embeddings... This may take 1-2 minutes for large files.');
      const ownerId = currentUser?.id || currentUser?._id || currentUser?.userId || null;
      const query = ownerId ? `?ownerId=${encodeURIComponent(ownerId)}` : '';
      const formData = new FormData();
      selectedFiles.forEach((f) => formData.append('files', f));
      if (collectionMode === 'new') formData.append('collectionName', newCollectionName.trim());
      else formData.append('collectionId', selectedCollectionId);
      const token = localStorage.getItem('token');

      // Use AbortController for proper timeout handling (2 minutes for embedding)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);

      const res = await fetch(`${API_BASE}/api/upload-docs${query}`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      const data = await res.json();
      if (data.success) {
        setMessage(`✅ ${data.message}`);
        setSelectedFiles([]);
        setNewCollectionName('');
        setSelectedCollectionId('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        fetchFiles();
        fetchCollections();
        setTimeout(() => {
          setUploadModalOpen(false);
          setMessage('');
        }, 1500);
      } else {
        setMessage('❌ ' + (data.message || 'Upload failed'));
      }
    } catch (err) {
      console.error('Upload error:', err);
      if (err.name === 'AbortError') {
        setMessage('❌ Upload timeout (2 min). File may still be processing. Check back in a moment.');
      } else {
        setMessage('❌ Upload failed: network or server error');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleAddFromUrl = async () => {
    const url = urlInput.trim();
    if (!url) {
      setMessage('Please enter a URL.');
      return;
    }
    if (!/^https?:\/\//i.test(url)) {
      setMessage('URL must start with http:// or https://');
      return;
    }
    setMessage('');
    setUrlUploading(true);
    try {
      const token = localStorage.getItem('token');
      const body = {
        url,
        ...(collectionMode === 'new' && newCollectionName.trim() ? { collectionName: newCollectionName.trim() } : {}),
        ...(collectionMode === 'existing' && selectedCollectionId ? { collectionId: selectedCollectionId } : {}),
      };
      const res = await fetch(`${API_BASE}/api/upload-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        setMessage('✅ Added from URL.');
        setUrlInput('');
        fetchFiles();
        fetchCollections();
        setTimeout(() => {
          setUploadModalOpen(false);
          setMessage('');
        }, 1500);
      } else {
        setMessage('❌ ' + (data.message || 'Failed'));
      }
    } catch (err) {
      console.error('URL upload error:', err);
      setMessage('❌ URL failed: network or server error');
    } finally {
      setUrlUploading(false);
    }
  };

  const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}" from your knowledge base? This cannot be undone.`)) return;
    try {
      setDeletingId(id);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/delete-knowledgebase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ id }),
      });
      const data = await res.json();
      if (data.success) {
        setMessage(`🗑️ Deleted: ${name}`);
        fetchFiles();
      } else setMessage('❌ Delete failed: ' + (data.message || 'Unknown error'));
    } catch (err) {
      console.error('Delete error:', err);
      setMessage('❌ Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const getCollectionName = (collectionId) => {
    if (collectionId === '_ungrouped') return 'Ungrouped';
    const c = collections.find((col) => String(col._id) === String(collectionId));
    return c ? c.name : `Collection`;
  };

  const startRenameCollection = (collectionId) => {
    const c = collections.find((col) => String(col._id) === String(collectionId));
    if (!c) return;
    setEditingCollectionId(String(collectionId));
    setRenameValue(String(c.name || ''));
  };

  const cancelRenameCollection = () => {
    setEditingCollectionId('');
    setRenameValue('');
  };

  const saveRenameCollection = async (collectionId) => {
    const nextName = renameValue.trim();
    if (!nextName) {
      setMessage('❌ Collection name cannot be empty.');
      return;
    }
    try {
      setRenamingId(String(collectionId));
      const token = localStorage.getItem('token');
      const ownerId = currentUser?.id || currentUser?._id || currentUser?.userId || null;
      const res = await fetch(`${API_BASE}/api/knowledgebase-collections/${collectionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ name: nextName, ...(ownerId ? { ownerId } : {}) }),
      });
      const data = await res.json();
      if (data.success) {
        setMessage('✅ Collection renamed.');
        cancelRenameCollection();
        fetchCollections();
      } else {
        setMessage(`❌ Rename failed: ${data.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Rename collection error:', err);
      setMessage('❌ Rename failed');
    } finally {
      setRenamingId('');
    }
  };

  const createTestKnowledgeBases = async () => {
    if (!window.confirm('Create two test knowledge bases with sample content?')) return;
    try {
      setMessage('Creating test knowledge bases...');
      setUploading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/create-test-knowledgebases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const data = await res.json();
      if (data.success) {
        setMessage(`✅ ${data.message}`);
        fetchFiles();
        fetchCollections();
        setTimeout(() => {
          setUploadModalOpen(false);
          setMessage('');
        }, 1500);
      } else setMessage('❌ ' + (data.message || 'Failed'));
    } catch (err) {
      setMessage('❌ ' + (err.message || 'Failed'));
    } finally {
      setUploading(false);
    }
  };

  const currentAccept = addSourceType === 'file' ? (FILE_TYPES.find((t) => t.id === selectedFileType)?.accept || ALL_ACCEPT) : '';

  return (
    <div className="h-full min-h-0 flex flex-col bg-slate-50 text-slate-900">
      <header className="shrink-0 z-30 bg-white/80 backdrop-blur border-b border-slate-200">
        <div className="h-16 w-full px-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[var(--freshroad-teal)]/10 flex items-center justify-center text-[var(--freshroad-teal)]">
              <Library className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-lg md:text-xl font-semibold text-slate-900">Knowledge Base</h1>
              <p className="text-xs text-slate-500">Documents and URLs for RAG</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setUploadModalOpen(true);
                setMessage('');
                setAddSourceType('file');
                setSelectedFiles([]);
                setUrlInput('');
              }}
              className="inline-flex items-center gap-2 rounded-xl bg-[var(--freshroad-teal)] text-white px-4 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Add content
            </button>
            <button
              onClick={() => {
                fetchFiles();
                fetchCollections();
              }}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 min-h-0 w-full overflow-y-auto overscroll-contain [scrollbar-gutter:stable]">
        <div className="px-4 py-6 max-w-4xl mr-auto text-left pb-10">
          {Object.keys(filesByCollection).length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm">
              <Library className="w-14 h-14 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600 font-medium">No documents yet</p>
              <p className="text-slate-500 text-sm mt-1">Add files or URLs to build your knowledge base.</p>
              <button
                onClick={() => setUploadModalOpen(true)}
                className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[var(--freshroad-teal)] text-white px-4 py-2.5 text-sm font-medium hover:opacity-90"
              >
                <Plus className="w-4 h-4" />
                Add content
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(filesByCollection).map(([collId, files]) => (
                <section key={collId} className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600">
                      <FolderPlus className="w-4 h-4" />
                    </div>
                      {editingCollectionId === String(collId) ? (
                        <div className="flex items-center gap-2 min-w-0">
                          <input
                            type="text"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            className="min-w-[220px] max-w-[420px] rounded-lg border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--freshroad-teal)]"
                            autoFocus
                          />
                          <button
                            type="button"
                            onClick={() => saveRenameCollection(collId)}
                            disabled={renamingId === String(collId)}
                            className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
                            title="Save name"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={cancelRenameCollection}
                            disabled={renamingId === String(collId)}
                            className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-50"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <h2 className="font-semibold text-slate-800 truncate">
                          {getCollectionName(collId)} <span className="text-slate-400 font-normal">({files.length})</span>
                        </h2>
                      )}
                    </div>
                    {collId !== '_ungrouped' && editingCollectionId !== String(collId) && (
                      <button
                        type="button"
                        onClick={() => startRenameCollection(collId)}
                        className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                        title="Rename collection"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="grid gap-2">
                    {files.map((f) => (
                      <div
                        key={f._id}
                        className="group flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:border-slate-300 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 flex-shrink-0">
                            <Library className="w-5 h-5" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 truncate">{f.originalName}</p>
                            <p className="text-xs text-slate-500">{formatDate(f.uploadedAt)}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDelete(f._id, f.originalName)}
                          disabled={deletingId === f._id}
                          className="opacity-0 group-hover:opacity-100 p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-all disabled:opacity-50"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}

          {message && (
            <p
              className={`mt-4 text-sm ${message.includes('✅') ? 'text-green-600' : message.includes('❌') || message.includes('🗑️') ? 'text-red-600' : 'text-slate-600'}`}
            >
              {message}
            </p>
          )}
        </div>
      </div>

      {/* Add content modal */}
      {uploadModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-lg font-semibold text-slate-900">Add to knowledge base</h2>
              <button
                onClick={() => {
                  setUploadModalOpen(false);
                  setSelectedFiles([]);
                  setUrlInput('');
                  setMessage('');
                }}
                className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Source type: File vs URL */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Source</label>
                <div className="flex rounded-xl border border-slate-200 p-1 bg-slate-50">
                  <button
                    type="button"
                    onClick={() => setAddSourceType('file')}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      addSourceType === 'file'
                        ? 'bg-white text-[var(--freshroad-teal)] shadow-sm border border-slate-200'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <UploadIcon className="w-4 h-4" />
                    Upload files
                  </button>
                  <button
                    type="button"
                    onClick={() => setAddSourceType('url')}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      addSourceType === 'url'
                        ? 'bg-white text-[var(--freshroad-teal)] shadow-sm border border-slate-200'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <LinkIcon className="w-4 h-4" />
                    From URL
                  </button>
                </div>
              </div>

              {addSourceType === 'file' ? (
                <>
                  {/* File type selector */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">File type</label>
                    <div className="flex flex-wrap gap-2">
                      {FILE_TYPES.map((t) => {
                        const Icon = t.icon;
                        return (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => setSelectedFileType(t.id)}
                            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                              selectedFileType === t.id
                                ? 'border-[var(--freshroad-teal)] bg-[var(--freshroad-teal)]/10 text-[var(--freshroad-teal)]'
                                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            {t.label} ({t.ext})
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Select files</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept={currentAccept}
                      onChange={handleFileChange}
                      className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[var(--freshroad-teal)]/10 file:text-[var(--freshroad-teal)] hover:file:bg-[var(--freshroad-teal)]/20 file:cursor-pointer cursor-pointer border border-slate-200 rounded-xl bg-slate-50 p-2"
                    />
                    {selectedFiles.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {selectedFiles.map((file, i) => (
                          <li key={i} className="flex items-center justify-between text-sm bg-slate-100 rounded-lg px-3 py-2">
                            <span className="truncate text-slate-700">{file.name}</span>
                            <button type="button" onClick={() => handleRemoveFile(i)} className="text-slate-400 hover:text-red-600 p-1">
                              <X className="w-4 h-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Page URL</label>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={urlInput}
                      onChange={(e) => setUrlInput(e.target.value)}
                      placeholder="https://example.com/page"
                      className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--freshroad-teal)] focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={handleAddFromUrl}
                      disabled={urlUploading || !urlInput.trim()}
                      className="rounded-xl bg-[var(--freshroad-teal)] text-white px-4 py-2.5 text-sm font-medium hover:opacity-90 disabled:opacity-50"
                    >
                      {urlUploading ? 'Adding…' : 'Add URL'}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1.5">Main article content is extracted. JS-only pages may not work.</p>
                </div>
              )}

              {/* Collection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Collection</label>
                <div className="flex gap-4 mb-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="collectionMode"
                      value="new"
                      checked={collectionMode === 'new'}
                      onChange={(e) => setCollectionMode(e.target.value)}
                      className="text-[var(--freshroad-teal)] focus:ring-[var(--freshroad-teal)]"
                    />
                    <span className="text-sm text-slate-700">New</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="collectionMode"
                      value="existing"
                      checked={collectionMode === 'existing'}
                      onChange={(e) => setCollectionMode(e.target.value)}
                      className="text-[var(--freshroad-teal)] focus:ring-[var(--freshroad-teal)]"
                    />
                    <span className="text-sm text-slate-700">Existing</span>
                  </label>
                </div>
                {collectionMode === 'new' ? (
                  <input
                    type="text"
                    value={newCollectionName}
                    onChange={(e) => setNewCollectionName(e.target.value)}
                    placeholder="Collection name"
                    className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--freshroad-teal)] focus:border-transparent"
                  />
                ) : (
                  <select
                    value={selectedCollectionId}
                    onChange={(e) => setSelectedCollectionId(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--freshroad-teal)] focus:border-transparent bg-white"
                  >
                    <option value="">Select collection</option>
                    {collections.map((col) => (
                      <option key={col._id} value={col._id}>
                        {col.name} ({col.fileCount ?? 0})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="flex gap-2 pt-2">
                {addSourceType === 'file' && (
                  <button
                    onClick={handleUpload}
                    disabled={uploading || selectedFiles.length === 0}
                    className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-[var(--freshroad-teal)] text-white py-3 text-sm font-medium hover:opacity-90 disabled:opacity-50"
                  >
                    <UploadIcon className="w-4 h-4" />
                    {uploading ? 'Uploading…' : `Upload ${selectedFiles.length} file(s)`}
                  </button>
                )}
                <button
                  onClick={createTestKnowledgeBases}
                  disabled={uploading || urlUploading}
                  className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Create test data
                </button>
              </div>

              {message && (
                <p
                  className={`text-sm ${message.includes('✅') ? 'text-green-600' : message.includes('❌') ? 'text-red-600' : 'text-slate-600'}`}
                >
                  {message}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadKnowledgeBase;
